import { toProperCase } from '@avalara/bat-apps-shared/helpers/index';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { useAppDispatch } from '../../../hooks/use-app-dispatch';
import { captureAbandonedSignup } from '../../../lib/helpers';
import { getCountries, getRegions } from '../../../lib/side-effects/avatax';
import { selectState } from '../../../selectors';

import type { Field } from '../../../../../types';
import type { AbandonedData } from '../../../../../types/marketing';

export const useAbandonedCart = (usingOfferFlow?: boolean) => {
  const {
    data: { marketingData },
    account: { isLoggedIn, billingContact, companyName: authenticatedCompanyName },
    offer: { offerFlow },
    sharedSignup: { signupForm },
    system: { activeFlow },
  } = useSelector(selectState);

  let authenticatedEmail: string;
  let authenticatedFirstName: string;
  let authenticatedLastName: string;
  let authenticatedAddress: string | null;
  let authenticatedCity: string | null;
  let authenticatedCountry: string | null;
  let authenticatedPostalCode: string | null;
  let authenticatedState: string | null;
  if (isLoggedIn && billingContact !== null && typeof billingContact !== 'string') {
    authenticatedEmail = billingContact.email || '';
    authenticatedFirstName = billingContact.firstName || '';
    authenticatedLastName = billingContact.lastName || '';
    authenticatedAddress = billingContact.street;
    authenticatedCity = billingContact.city;
    authenticatedCountry = billingContact.country;
    authenticatedPostalCode = billingContact.postalCode;
    authenticatedState = billingContact.state;
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    const abandonedSignupOptions = {
      company: {
        address: isLoggedIn ? authenticatedAddress : signupForm.street1,
        city: isLoggedIn ? authenticatedCity : signupForm.city,
        company_name: isLoggedIn ? authenticatedCompanyName : signupForm.companyName,
        country: isLoggedIn ? authenticatedCountry : signupForm.country,
        postal_code: isLoggedIn ? authenticatedPostalCode : signupForm.postalCode,
        state: isLoggedIn ? authenticatedState : signupForm.state,
      },
      email: isLoggedIn ? authenticatedEmail : signupForm.email,
      firstName: isLoggedIn ? authenticatedFirstName : signupForm.firstName,
      lastName: isLoggedIn ? authenticatedLastName : signupForm.lastName,
      marketingData,
    };

    let abandonedPurchaseHandler: (options: Event | AbandonedData) => void;
    if (usingOfferFlow) {
      if (!offerFlow) return () => {};
      abandonedPurchaseHandler = captureAbandonedSignup.bind(null, {
        ...abandonedSignupOptions,
        offerFlow,
        isLoggedIn,
      });
    } else {
      abandonedPurchaseHandler = captureAbandonedSignup.bind(null, {
        ...abandonedSignupOptions,
        activeFlow,
      });
    }

    window.addEventListener('unload', abandonedPurchaseHandler);
    return () => {
      window.removeEventListener('unload', abandonedPurchaseHandler);
    };
  }, [
    activeFlow,
    offerFlow,
    signupForm.firstName,
    signupForm.lastName,
    signupForm.companyName,
    signupForm.email,
    signupForm.street1,
    signupForm.city,
    signupForm.country,
    signupForm.postalCode,
    signupForm.state,
    marketingData.campaignId,
    marketingData.campaignName,
    marketingData.connector,
    marketingData.lsmr,
    marketingData.lso,
    marketingData.partnerSource,
    marketingData.salesRepId,
  ]);
};

type RegionsAndCountries = {
  formattedRegions: Array<Field>;
  formattedCountries: Array<Field>;
};

export const useFetchRegionsAndCountries = (shouldFetchRegionsAndCountries = true): RegionsAndCountries => {
  const {
    data: { countries, regions },
    sharedSignup: {
      signupForm: { country },
    },
  } = useSelector(selectState);
  const dispatch = useAppDispatch();

  // biome-ignore lint/correctness/useExhaustiveDependencies: leaving as is
  useEffect(() => {
    if (shouldFetchRegionsAndCountries) {
      dispatch(getCountries());
      dispatch(getRegions(country));
    }
  }, [country]);

  const selectedCountryRegions = regions[country] || [];
  const formattedCountries = countries.map(({ code, name }) => ({ value: code, display: toProperCase(name) }));
  const formattedRegions = selectedCountryRegions.map(({ code, name }) => ({
    value: code,
    display: toProperCase(name),
  }));

  return { formattedCountries, formattedRegions };
};
